import type { FunctionComponent, ReactNode } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { IconCrossSmall } from '~/ui/core/icons';
import { TileControlGroup } from '~/ui/core/tile/Tile';
import { LanguagePicker } from '~/ui/domain/language-picker/LanguagePicker';

import { getLearningLanguageLabel } from '../helpers';

export const SidepanelHeaderTitle: FunctionComponent<{ readonly children: ReactNode }> = ({ children }) => (
    <div className="flex min-w-full flex-col gap-s" data-testid="learning-opportunity-header-title">
        {children}
    </div>
);

export const SidepanelHeaderNavigation: FunctionComponent<{
    readonly activeLanguage: string;
    readonly availableLanguages: readonly string[];
    readonly actions?: ReactNode;
    readonly isLanguagePickerDisabled?: boolean;
    readonly onClose: () => void;
    readonly onLanguageChange: (language: string) => void;
    readonly textLanguages?: readonly string[];
}> = ({
    actions,
    activeLanguage,
    availableLanguages,
    isLanguagePickerDisabled = false,
    onClose,
    onLanguageChange,
    textLanguages = [],
}) => {
    const translate = useTranslate();
    const languageLabel = (code: string): string => getLearningLanguageLabel(code, translate) ?? code;

    return (
        <div className="flex w-full items-center justify-between gap-l" data-testid="sidepanel-header-navigation">
            <div className="flex items-center gap-l">
                <TileControlGroup>
                    <Button aria-label="close" iconOnly={true} size="sm" variant="secondary" onClick={onClose}>
                        <IconCrossSmall />
                    </Button>
                </TileControlGroup>
                <div className="h-xxl w-px bg-border-subtle" />
                <LanguagePicker
                    activeText={languageLabel(activeLanguage)}
                    disabled={isLanguagePickerDisabled}
                    languages={availableLanguages.map((code) => ({
                        id: code,
                        name:
                            code !== activeLanguage && textLanguages.includes(code) ? (
                                <span className="flex items-center justify-between gap-l">
                                    {languageLabel(code)}
                                    <span className="text-body-xxs text-text-secondary">
                                        {translate('pages.learning-opportunities.translation-original')}
                                    </span>
                                </span>
                            ) : (
                                languageLabel(code)
                            ),
                    }))}
                    onLanguageChange={onLanguageChange}
                />
            </div>
            {actions !== undefined && <div className="flex shrink-0 items-center gap-s">{actions}</div>}
        </div>
    );
};
